import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Target, MapPin, BarChart3, TrendingUp, Map, Users,
  History, Download, Settings, ShieldCheck, GitCompareArrows, Building2, Compass, Goal,
  LayoutGrid, LineChart, UserSearch, Wallet, Rocket, HeartPulse, Calculator,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTenant } from '@/context/TenantContext';
import { PoweredBy } from '@/components/common/PoweredBy';
import { SharitiLockup } from '@/components/common/SharitiLogo';

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/campagnes', label: 'Campagnes', icon: Target },
  { to: '/operations', label: 'Op\u00e9rations', icon: MapPin },
  { to: '/performances', label: 'Performances', icon: BarChart3 },
  { to: '/comparaison', label: 'Comparaison', icon: GitCompareArrows },
  { to: '/objectifs', label: 'Objectifs', icon: Goal },
  { to: '/analyses', label: 'Analyses', icon: TrendingUp },
  { to: '/carte', label: 'Carte', icon: Map },
  { to: '/responsables', label: 'Responsables', icon: Users },
  { to: '/historique', label: 'Historique', icon: History },
  { to: '/exports', label: 'Exports', icon: Download },
  { to: '/parametres', label: 'Param\u00e8tres', icon: Settings },
];

// Aggregated "all associations" navigation (admin global pilotage).
const AGG_NAV = [
  { to: '/consolidation', label: 'Vue consolidée', icon: Building2 },
  { to: '/comparatif', label: 'Comparatif assos', icon: LayoutGrid },
  { to: '/campagnes', label: 'Campagnes', icon: Target },
  { to: '/operations', label: 'Opérations', icon: MapPin },
  { to: '/performances', label: 'Performances', icon: BarChart3 },
  { to: '/comparaison', label: 'Comparaison', icon: LineChart },
  { to: '/objectifs', label: 'Objectifs', icon: Goal },
  { to: '/analyses', label: 'Analyses', icon: TrendingUp },
  { to: '/carte', label: 'Carte', icon: Map },
  { to: '/responsables', label: 'Responsables', icon: Users },
  { to: '/recruteurs', label: 'Recruteurs', icon: UserSearch },
  { to: '/territoires', label: 'Territoires', icon: Compass },
  { to: '/suivi-social', label: 'Suivi Social', icon: Wallet },
  { to: '/plan-strategique', label: 'Plan stratégique', icon: Rocket },
  { to: '/attrition', label: 'Attrition', icon: HeartPulse },
  { to: '/admin', label: 'Administration', icon: ShieldCheck },
];

// Tabs reserved for SHARITI internal use — never shown to association users.
const ASSOC_HIDDEN = new Set(['/carte', '/historique', '/responsables']);

const Initials = ({ name, color }) => {
  const initials = (name || 'SH').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl font-display text-sm font-bold" style={{ background: color || '#EE722E', color: 'var(--tenant-on-brand)' }} data-testid="tenant-logo">
      {initials}
    </div>
  );
};

export const SidebarContent = ({ onNavigate }) => {
  const { isAdmin, isCampaignManager, user, logout } = useAuth();
  const { association, isAll } = useTenant();

  const consolidatedItem = { to: '/consolidation', label: 'Vue consolidée', icon: Building2, consolidated: true };
  const territoiresItem = { to: '/territoires', label: 'Territoires', icon: Compass, consolidated: true };
  const socialItem = { to: '/suivi-social', label: 'Suivi Social', icon: Wallet };
  const strategyItem = { to: '/plan-strategique', label: 'Plan stratégique', icon: Rocket };
  const attritionItem = { to: '/attrition', label: 'Attrition', icon: HeartPulse };
  const rexItem = { to: '/pilotage-rex', label: 'Pilotage REX', icon: Calculator };
  const adminItem = { to: '/admin', label: 'Administration', icon: ShieldCheck };

  // Modules confidentiels : STRICTEMENT réservés au propriétaire (administrateur
  // total) OU à un admin à qui le propriétaire a explicitement accordé l'accès
  // (flag === true). Modèle « opt-in » : par défaut MASQUÉS (aucun menu, aucune
  // trace) pour tout autre utilisateur.
  const isOwner = user?.is_owner === true;
  const canViewSocial = isOwner || user?.can_view_social === true;
  const canViewStrategy = isOwner || user?.can_view_strategy === true;

  let items;
  if (isAll) {
    items = AGG_NAV.filter((it) => (it.to !== '/suivi-social' || canViewSocial) && ((it.to !== '/plan-strategique' && it.to !== '/attrition') || canViewStrategy));
    if (isOwner) {
      const ai = items.findIndex((it) => it.to === '/admin');
      items = ai >= 0 ? [...items.slice(0, ai), rexItem, ...items.slice(ai)] : [...items, rexItem];
    }
  } else if (isAdmin) {
    items = [...NAV, consolidatedItem, territoiresItem, ...(canViewSocial ? [socialItem] : []), ...(canViewStrategy ? [strategyItem, attritionItem] : []), ...(isOwner ? [rexItem] : []), adminItem];
  } else if (isCampaignManager) {
    // Campaign managers: association tabs + a scoped "Recruteurs" view of their team.
    const base = NAV.filter((it) => !ASSOC_HIDDEN.has(it.to));
    const recruteursItem = { to: '/recruteurs', label: 'Recruteurs', icon: UserSearch };
    const idx = base.findIndex((it) => it.to === '/performances');
    items = idx >= 0
      ? [...base.slice(0, idx + 1), recruteursItem, ...base.slice(idx + 1)]
      : [...base, recruteursItem];
  } else {
    // Association users: internal-only tabs (Carte, Historique) are removed.
    items = NAV.filter((it) => !ASSOC_HIDDEN.has(it.to));
  }

  const handleNav = () => {
    onNavigate?.();
  };

  return (
    <div className="flex h-full flex-col">
      {/* SHARITI product identity (always present) */}
      <div className="px-4 pt-5">
        <SharitiLockup size="md" />
      </div>

      {/* Active workspace (co-branding or consolidated) */}
      <div className="px-4 pb-3 pt-4">
        {isAll ? (
          <div className="flex items-center gap-2.5 rounded-2xl border bg-white/60 px-2.5 py-2 card-shadow" data-testid="tenant-consolidated">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ background: 'var(--tenant-accent-500)' }}>
              <Building2 className="h-4.5 w-4.5" style={{ color: 'var(--tenant-on-accent)' }} />
            </div>
            <div className="min-w-0">
              <p className="break-words font-display text-[13px] font-semibold leading-tight text-foreground" data-testid="tenant-name">
                Toutes les associations
              </p>
              <p className="text-[10px] text-muted-foreground">Pilotage global · Admin</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2.5 rounded-2xl border bg-white/60 px-2.5 py-2 card-shadow">
            {association?.logo_url
              ? <img src={association.logo_url} alt="" className="h-9 w-9 rounded-xl object-contain" data-testid="tenant-logo" />
              : <Initials name={association?.name} color={association?.primary_color} />}
            <div className="min-w-0">
              <p className="break-words font-display text-[13px] font-semibold leading-tight text-foreground" data-testid="tenant-name">
                {association?.short_name || association?.name || 'SHARITI'}
              </p>
              <p className="text-[10px] text-muted-foreground" data-testid="tenant-subtitle">
                {isCampaignManager ? `Chargé de campagne · ${user?.name || ''}` : 'Espace de pilotage'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-1 overflow-y-auto px-3">
        {items.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end} onClick={handleNav}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            data-testid={`nav-${it.label.toLowerCase().normalize('NFD').replace(/[^a-z]/g, '')}`}>
            <it.icon className="nav-icon h-[18px] w-[18px]" />
            <span className="font-medium">{it.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="mt-2 border-t px-4 py-3">
        <button onClick={logout} className="mb-3 text-xs text-muted-foreground hover:text-foreground" data-testid="logout-button">
          Se déconnecter
        </button>
        <PoweredBy association={isAll ? null : association?.short_name} />
      </div>
    </div>
  );
};
